import React from 'react'

import { Icon, Select } from '@clientbase/clientbase-library'
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography
} from '@mui/material'

import { ExtraFieldItem } from 'components/ExtraFieldsManager'

import { theme } from 'styles/theme'

import {
  BASIC_SECTION,
  conditionCandidates,
  copySourceOptions,
  EXTRAS_SECTION,
  FieldDisplaySettings as Settings,
  fieldOptions,
  FormSection,
  conditionProblem,
  isTextField,
  sectionOf
} from './formLayout.utils'

// O MUI Select não mostra item com value '': "Informações extras" usa este
const EXTRAS_VALUE = 'extras'

interface FieldDisplaySettingsProps {
  field: ExtraFieldItem
  fields: ExtraFieldItem[]
  sections: FormSection[]
  basicFields: Record<string, boolean>
  onChange: (settings: Settings) => void
}

const sectionLabel = (sectionKey: string, sections: FormSection[]) => {
  if (sectionKey === BASIC_SECTION) return 'Dados básicos'
  if (sectionKey === EXTRAS_SECTION) return 'Informações extras'
  const section = sections.find((s) => s.uuid === sectionKey)
  return section?.title?.trim() || 'Seção sem nome'
}

// SC-11487: em que seção o campo aparece, quando aparece e se o botão
// "Mesmos dados do contratante" preenche ele.
const FieldDisplaySettings = ({
  field,
  fields,
  sections,
  basicFields,
  onChange
}: FieldDisplaySettingsProps) => {
  const [open, setOpen] = React.useState(false)
  const settings = field.display_settings || {}
  const sectionKey = sectionOf(field)
  const section = sections.find((s) => s.uuid === sectionKey)
  const candidates = conditionCandidates(field, fields, sections)
  const condition = settings.visible_when
  const trigger = fields.find((f) => f.uuid === condition?.custom_field_uuid)
  const problem = conditionProblem(field, fields, sections)
  const canCopy = !!section?.copy_customer_data && isTextField(field)
  const copyOptions = copySourceOptions(basicFields)

  const summary = [
    sectionLabel(sectionKey, sections),
    condition ? 'Condicional' : null,
    settings.copy_from
      ? `Copia ${
          copyOptions.find((o) => o.value === settings.copy_from)?.label ||
          'dado do contratante'
        }`
      : null
  ]
    .filter(Boolean)
    .join(' · ')

  const update = (patch: Partial<Settings>) => {
    const next: Settings = { ...settings, ...patch }
    ;(Object.keys(next) as Array<keyof Settings>).forEach((key) => {
      if (next[key] === undefined) delete next[key]
    })
    onChange(next)
  }

  return (
    <Box ml="5px" mt={0.5}>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`Exibição do campo ${field.name}`}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((prev) => !prev)
          }
        }}
        sx={{ cursor: 'pointer', width: 'fit-content', ml: '5px' }}
      >
        <Icon
          icon={open ? 'keyboardArrowUp' : 'keyboardArrowDown'}
          sx={{ fontSize: '18px', color: theme.palette.neutral[400] }}
        />
        <Typography variant="smLight" color={theme.palette.neutral[400]}>
          Exibição: {summary}
        </Typography>
      </Stack>
      {problem && (
        <Typography
          variant="xsLight"
          color={theme.palette.error[300]}
          display="block"
          ml="28px"
        >
          {problem}
        </Typography>
      )}

      <Collapse in={open} unmountOnExit>
        <Stack
          gap={1.5}
          mt={1}
          p={1.5}
          borderRadius="8px"
          border={`1px solid ${theme.palette.neutral[700]}`}
        >
          <Select
            fullWidth
            size="small"
            label="Seção"
            value={sectionKey === EXTRAS_SECTION ? EXTRAS_VALUE : sectionKey}
            onChange={(event) => {
              const value = String(event.target.value)
              const nextSection = value === EXTRAS_VALUE ? undefined : value
              const keepsCopy = sections.find(
                (s) => s.uuid === nextSection
              )?.copy_customer_data
              update({
                section: nextSection,
                copy_from: keepsCopy ? settings.copy_from : undefined
              })
            }}
          >
            <MenuItem value={BASIC_SECTION}>Dados básicos</MenuItem>
            {sections.map((s) => (
              <MenuItem key={s.uuid} value={s.uuid}>
                {s.title.trim() || 'Seção sem nome'}
              </MenuItem>
            ))}
            <MenuItem value={EXTRAS_VALUE}>Informações extras</MenuItem>
          </Select>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!condition}
                  disabled={!condition && candidates.length === 0}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      update({ visible_when: undefined })
                      return
                    }
                    update({
                      visible_when: {
                        custom_field_uuid: candidates[0].uuid,
                        values: []
                      }
                    })
                  }}
                />
              }
              label={
                <Typography variant="smLight">
                  Exibir somente conforme a resposta de outro campo
                </Typography>
              }
            />
            {!condition && candidates.length === 0 && (
              <Typography
                variant="xsLight"
                color={theme.palette.neutral[400]}
                display="block"
              >
                Coloque antes deste um campo de seleção única ou multi-seleção
                para usar como condição.
              </Typography>
            )}
          </Box>

          {condition && (
            <Stack gap={1.5}>
              <Select
                fullWidth
                size="small"
                label="Campo"
                value={condition.custom_field_uuid}
                onChange={(event) =>
                  update({
                    visible_when: {
                      custom_field_uuid: String(event.target.value),
                      values: []
                    }
                  })
                }
              >
                {[
                  ...candidates,
                  ...(trigger && !candidates.includes(trigger) ? [trigger] : [])
                ].map((candidate) => (
                  <MenuItem key={candidate.uuid} value={candidate.uuid}>
                    {candidate.name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                fullWidth
                size="small"
                multiple
                displayEmpty
                label="For respondido com"
                value={condition.values}
                renderValue={(selected) => {
                  const values = selected as string[]
                  if (values.length === 0) return 'Escolha as opções'
                  const options = trigger ? fieldOptions(trigger) : []
                  return values
                    .map((v) => options.find((o) => o.value === v)?.label || v)
                    .join(', ')
                }}
                onChange={(event) => {
                  const value = event.target.value
                  update({
                    visible_when: {
                      ...condition,
                      values:
                        typeof value === 'string'
                          ? value.split(',')
                          : (value as string[])
                    }
                  })
                }}
              >
                {(trigger ? fieldOptions(trigger) : []).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      size="small"
                      checked={condition.values.includes(option.value)}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          )}

          {canCopy && (
            <Select
              fullWidth
              size="small"
              displayEmpty
              label='Preencher pelo botão "Mesmos dados do contratante"'
              value={settings.copy_from || ''}
              onChange={(event) =>
                update({ copy_from: String(event.target.value) || undefined })
              }
            >
              <MenuItem value="">Não preencher</MenuItem>
              {copyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}
        </Stack>
      </Collapse>
    </Box>
  )
}

export default FieldDisplaySettings
